import { AppThunk, RootState } from "store";
import { ApiLoadingStatus } from "enum/api-loading-status.enum";
import { adjustDateRangeToLocalDay } from "utils/adjustDateRangeToLocalDay";
import {
  fetchPickups,
  fetchEligiblePickupShipments,
  fetchPickupAddresses,
  fetchShipmentsRelatedToPickups,
} from "services/api/shipments";
import { ShipmentStatusCategories } from "constants/shipment-statuses.config";
import {
  setAddressStatus,
  setShipmentStatus,
  setPickupsStatus,
  setShipmentsFromPickupsStatus,
  setAddressData,
  setShipmentData,
  setPickupsData,
  setShipmentsFromPickupsData,
  setPickupsQuerySearch,
  setPickupsQueryCarrierName,
  setPickupsQueryOffset,
  IPickupSliceFilters,
} from "store/pickupSlice/index.slice";

export const fetchPickUpAddressesThunk = (): AppThunk => {
  return async (dispatch, getState) => {
    await dispatch(setAddressStatus(ApiLoadingStatus.LOADING));
    const {
      authSlice: { salesAccountUUID },
      pickupSlice,
    } = getState() as RootState;

    const {
      filters: { fromDate, toDate },
    } = pickupSlice.addressQuery;
    // Use local calendar date so users see their full day, not the UTC day
    const { adjustedFromDate, adjustedToDate } = adjustDateRangeToLocalDay(
      fromDate,
      toDate,
    );
    if (salesAccountUUID) {
      const { data, error, meta } = await fetchPickupAddresses(
        salesAccountUUID,
        {
          filters: {
            ...(adjustedFromDate && { fromDate: adjustedFromDate }),
            ...(adjustedToDate && { toDate: adjustedToDate }),
          },
        },
      );
      await dispatch(setAddressStatus(ApiLoadingStatus.SUCCESS));

      if (!error) {
        dispatch(setAddressData({ data, meta }));
      }
    }
  };
};

export const fetchEligiblePickUpShipmentsThunk = (): AppThunk => {
  return async (dispatch, getState) => {
    await dispatch(setShipmentStatus(ApiLoadingStatus.LOADING));
    const {
      authSlice: { salesAccountUUID },
      pickupSlice: { addressQuery, addressData },
      tableSlice: { numberOfRowsSelected },
    } = getState() as RootState;

    const {
      offset,
      filters: { fromDate, toDate },
    } = addressQuery;
    // Use local calendar date so users see their full day, not the UTC day
    const { adjustedFromDate, adjustedToDate } = adjustDateRangeToLocalDay(
      fromDate,
      toDate,
    );
    let selectedAddress = null;

    if (addressData.ups.some((upsAddress) => !upsAddress.hide)) {
      selectedAddress = addressData.ups.find((upsAddress) => !upsAddress.hide);
    } else if (addressData.fedex.some((fedexAddress) => !fedexAddress.hide)) {
      selectedAddress = addressData.fedex.find(
        (fedexAddress) => !fedexAddress.hide,
      );
    } else if (addressData.dhl.some((dhlAddress) => !dhlAddress.hide)) {
      selectedAddress = addressData.dhl.find(
        (dhlAddress) => !dhlAddress.hide,
      );
    }
    if (salesAccountUUID) {
      const { data, error, meta } = await fetchEligiblePickupShipments(
        salesAccountUUID,
        {
          offset,
          filters: {
            statuses: ShipmentStatusCategories.COMPLETE,
            ...(adjustedFromDate && { fromDate: adjustedFromDate }),
            ...(adjustedToDate && { toDate: adjustedToDate }),
            ...(selectedAddress?.fromAddressCity && {
              fromAddressCity: selectedAddress?.fromAddressCity,
            }),
            ...(selectedAddress?.fromAddressCountryIso && {
              fromAddressCountryIso: selectedAddress?.fromAddressCountryIso,
            }),
            ...(selectedAddress?.fromAddressStreet1 && {
              fromAddressStreet1: selectedAddress?.fromAddressStreet1,
            }),
            ...(selectedAddress?.fromAddressZip && {
              fromAddressZip: selectedAddress?.fromAddressZip,
            }),
            ...(selectedAddress?.carrierId && {
              carrierID: selectedAddress?.carrierId,
            }),
          },
          limit: numberOfRowsSelected.value,
        },
      );

      await dispatch(setShipmentStatus(ApiLoadingStatus.SUCCESS));

      if (!error) {
        dispatch(setShipmentData({ data, meta }));
      }
    }
  };
};

export const fetchPickupsThunk = (): AppThunk => {
  return async (dispatch, getState) => {
    await dispatch(setPickupsStatus(ApiLoadingStatus.LOADING));
    const {
      authSlice: { salesAccountUUID },
      pickupSlice,
      tableSlice: { numberOfRowsSelected },
    } = getState() as RootState;

    const {
      offset,
      filters: { search, fromDate, toDate, carrierName },
      sort,
    } = pickupSlice.pickupsQuery;
    // Use local calendar date so users see their full day, not the UTC day
    const { adjustedFromDate, adjustedToDate } = adjustDateRangeToLocalDay(
      fromDate,
      toDate,
    );
    if (salesAccountUUID) {
      const { data, error, meta } = await fetchPickups(salesAccountUUID, {
        offset,
        filters: {
          statuses: ShipmentStatusCategories.COMPLETE,
          ...(search && { search }),
          ...(carrierName?.length && { carrierName: carrierName.join(",") }),
          ...(adjustedFromDate && { fromDate: adjustedFromDate }),
          ...(adjustedToDate && { toDate: adjustedToDate }),
        },
        sort,
        limit: numberOfRowsSelected.value,
      });

      await dispatch(setPickupsStatus(ApiLoadingStatus.SUCCESS));
      if (!error) {
        dispatch(setPickupsData({ data, meta }));
      }
    }
  };
};

export const searchPickupsThunk = (query: string): AppThunk => {
  return async (dispatch) => {
    // A stale offset from a previous page survives a new search otherwise -
    // the request goes out for e.g. offset=50 against a now much smaller
    // filtered result set and comes back empty even though matches exist.
    await dispatch(setPickupsQueryOffset(0));
    await dispatch(setPickupsQuerySearch(query));

    dispatch(fetchPickupsThunk());
  };
};

export const changePickupsCarrierFilterThunk = (
  carrierName: IPickupSliceFilters["carrierName"],
): AppThunk => {
  return async (dispatch) => {
    await dispatch(setPickupsQueryOffset(0));
    await dispatch(setPickupsQueryCarrierName(carrierName));

    dispatch(fetchPickupsThunk());
  };
};

export const fetchShipmentsRelatedToPickupsThunk = (): AppThunk => {
  return async (dispatch, getState) => {
    await dispatch(setShipmentsFromPickupsStatus(ApiLoadingStatus.LOADING));
    const {
      authSlice: { salesAccountUUID },
      pickupSlice,
      tableSlice: { numberOfRowsSelected },
    } = getState() as RootState;

    const { offset, sort } = pickupSlice.shipmentsFromPickupsQuery;
    const { selectedShipment } = pickupSlice;
    const { shipmentIDs } = selectedShipment;
    if (salesAccountUUID) {
      const { data, error, meta } = await fetchShipmentsRelatedToPickups(
        salesAccountUUID,
        {
          offset,
          filters: {
            statuses: ShipmentStatusCategories.COMPLETE,
            shipmentIDs,
          },
          sort,
          limit: numberOfRowsSelected.value,
        },
      );

      await dispatch(setShipmentsFromPickupsStatus(ApiLoadingStatus.SUCCESS));
      if (!error) {
        dispatch(setShipmentsFromPickupsData({ data, meta }));
      }
    }
  };
};
